import { differenceInMonths } from 'date-fns';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { getPropertiesList } from 'entities/Property/model/selectors/PropertySelectors';
import {
    type CategoryBreakdownDto,
    type ProductsResponseDto,
    type SavingsInsightsSummaryDto,
} from 'entities/ReportSavingsSummary';
import { useSavingsSummaryReportVisible } from 'shared/lib/hooks/useSavingsSummaryReportVisible';
import { parseUtcIsoToDate } from 'shared/lib/reportDatePresets';
import { useSavingsSummaryDrillInData } from 'widgets/SavingsSummaryReport/model/hooks/useSavingsSummaryDrillInData';

interface UseReportsPageSavingsSummaryPropertyResult {
    isSavingsSummaryVisible: boolean | undefined;
    propertyId: string | undefined;
    propertyName: string;
    loading: boolean;
    summary: SavingsInsightsSummaryDto | null;
    byCategory: CategoryBreakdownDto[];
    products: ProductsResponseDto | null;
    productsCount: number;
    totalSaved: number;
    savingsPerUnit: number | null;
    savingsPerUnitPerMonth: number | null;
    orderCount: number;
    savingsPerOrder: number | null;
    dateFrom: string;
    dateTo: string;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
}

export const useReportsPageSavingsSummaryProperty =
    (): UseReportsPageSavingsSummaryPropertyResult => {
        const { propertyId } = useParams<{ propertyId: string }>();
        const location = useLocation();
        const isSavingsSummaryVisible = useSavingsSummaryReportVisible();
        const properties = useSelector(getPropertiesList);

        const locationState = location.state as {
            propertyName?: string;
        } | null;
        const propertyNameFromState = locationState?.propertyName;

        const {
            loading,
            summary,
            byCategory,
            products,
            totalSaved,
            productsCount,
            orderCount,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
        } = useSavingsSummaryDrillInData({
            dimension: 'property',
            id: propertyId,
        });

        const propertyName = useMemo(() => {
            if (propertyNameFromState) {
                return propertyNameFromState;
            }

            const fromSummary = summary?.byPropertiesData?.find(
                (item) => item.propertyId === propertyId,
            )?.propertyName;
            if (fromSummary) {
                return fromSummary;
            }

            const fromPropertiesList = properties?.find(
                (item) => item.id === propertyId,
            );
            const fromPropertiesListName =
                fromPropertiesList?.propertyNameMarketing ||
                fromPropertiesList?.propertyNameLegal;
            if (fromPropertiesListName) {
                return fromPropertiesListName;
            }

            return propertyId ?? '';
        }, [propertyId, propertyNameFromState, properties, summary]);

        const unitCount = useMemo(() => {
            if (!propertyId) {
                return null;
            }

            const property = properties?.find((item) => item.id === propertyId);
            if (!property || typeof property.unitCount !== 'number') {
                return null;
            }

            return property.unitCount;
        }, [properties, propertyId]);

        const monthsInRange = Math.max(
            1,
            differenceInMonths(
                parseUtcIsoToDate(dateTo),
                parseUtcIsoToDate(dateFrom),
            ) + 1,
        );

        const savingsPerUnit =
            unitCount != null && unitCount > 0 ? totalSaved / unitCount : null;

        const savingsPerUnitPerMonth =
            unitCount > 0 && monthsInRange > 0
                ? totalSaved / unitCount / monthsInRange
                : null;

        const savingsPerOrder = orderCount > 0 ? totalSaved / orderCount : null;

        return {
            isSavingsSummaryVisible,
            propertyId,
            propertyName,
            loading,
            summary,
            byCategory,
            products,
            productsCount,
            totalSaved,
            savingsPerUnit,
            savingsPerUnitPerMonth,
            orderCount,
            savingsPerOrder,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
        };
    };
